import type { TFunction } from "i18next";
import type { CompetitorKpiCard } from "@nexus/contracts";
import { formatBrandName, formatCountValue } from "./format";

export type InsightVerdict = "strength" | "gap" | "watch";

export interface KpiInsight {
  verdict: InsightVerdict;
  sentence: string;
}

/** More than 5% ahead of the main competitor. */
const STRENGTH_RATIO = 1.05;
/** More than 5% behind the main competitor. */
const GAP_RATIO = 0.95;

/**
 * Pre-written insight for one competitor KPI card: you against the main competitor.
 * Returns null when either value is 0, since the copy states a ratio between them.
 */
export function buildCompetitorInsight(t: TFunction, card: CompetitorKpiCard): KpiInsight | null {
  if (card.you <= 0 || card.mainCompetitor <= 0) return null;

  const verdict = toVerdict(card.you / card.mainCompetitor);
  const params = {
    you: formatCountValue(card.you),
    main: formatCountValue(card.mainCompetitor),
    mainName: formatBrandName(card.brandLabels.mainCompetitor),
    ratio: formatRatio(card.you, card.mainCompetitor),
  };

  if (verdict === "watch") {
    return {
      verdict,
      sentence: t("dashboard.insights.competitors.level", {
        ...params,
        kpi: card.label.toLocaleLowerCase(),
      }),
    };
  }

  const direction = verdict === "strength" ? "ahead" : "behind";
  return {
    verdict,
    sentence: t(`dashboard.insights.competitors.${card.metric}.${direction}`, params),
  };
}

function toVerdict(ratio: number): InsightVerdict {
  if (ratio > STRENGTH_RATIO) return "strength";
  if (ratio < GAP_RATIO) return "gap";
  return "watch";
}

function formatRatio(a: number, b: number): string {
  return (Math.max(a, b) / Math.min(a, b)).toFixed(1);
}
